import type { ProductGroupResource } from "../../product-group";
import type { VariantResource } from "../../variant";

import type { NoteResource } from "../../note";
import type { AttributeValueResource } from "../../attribute-value";
import {
  type PriceListItemCalculateType,
  type PriceListItemResource,
} from "../../price-list-item";
import type { AttributeResource } from "../../attribute";
import type { MediaAttributes } from "../../../common.ts";
import type { VatResource } from "../../vat";
import type { UnitResource } from "../../unit";
import type { MarketplaceResource } from "../../marketplace";
import type { ConditionResource } from "../../condition";
import type { ManufacturerResource } from "../../manufacturer";

export enum ProductStockFlagEnum {
  Standard = "standard",
  OfflineIfSoldOut = "offline_if_sold_out",
  NotOrderableIfSoldOut = "not_orderable_if_sold_out",
}
/** Leistungsart (P1-3); null = nicht festgelegt. */
export enum SupplyTypeEnum {
  Delivery = "delivery",
  OtherService = "other_service",
}
/** Leistungsortregel einer sonstigen Leistung (P1-3); null = nicht geprüft. */
export enum PlaceOfSupplyRuleEnum {
  General = "general",
  Special = "special",
}
export enum ProductType {
  SINGLE = "single",
  SET = "set",
  MULTIPLE = "multiple",
  COMPLEX = "complex",
}

export enum ProductStatusEnum {
  Active = "active",
  Inactive = "inactive",
  Blocked = "blocked",
}

export interface ProductResource {
  id: string;
  gid: string;
  sku: string;
  title: string;
  type: ProductType;
  condition_id?: string | null;
  condition?: ConditionResource | null;
  unit?: UnitResource | null;
  sizeUnit?: UnitResource | null;
  volumeUnit?: UnitResource | null;
  manufacturer?: ManufacturerResource | null;
  attributes?: AttributeResource[];
  manufacturer_id?: string;
  price: string;
  short_description: string;
  long_description: string;
  in_stock_information: string;
  out_of_stock_information: string;
  status: ProductStatusEnum;
  stock_flag: ProductStockFlagEnum;
  supply_type: SupplyTypeEnum | null;
  place_of_supply_rule: PlaceOfSupplyRuleEnum | null;
  processing_time_id: string;
  is_short_description_printed_in_documents: boolean;
  has_best_before_date: boolean;
  is_intangible_product: boolean;
  is_electron_return_option: boolean;
  is_customizable_product: boolean;
  weight: string;
  weight_unit_id: string | null;
  weight_unit?: UnitResource | null;
  length: string;
  width: string;
  amount_of_unit_per_package: string;
  height: string;
  size_unit_id: string | null;
  volume: string;
  volume_unit_id: string | null;
  unit_quantity: string;
  unit_id: string | null;
  recommended_retail_price: string;
  blockedMarketplaces?: MarketplaceResource[];
  marketplaces?: MarketplaceResource[];
  variants?: VariantResource[];
  attributeValues?: AttributeValueResource[];
  productGroups?: ProductGroupResource[];
  configNumbers?: Record<string, { value: string }>;
  notes: NoteResource[];
  marketplaceData?: {
    [marketplaceId: string]: {
      title?: string;
      title_inherited: boolean;
      short_description?: string;
      short_description_inherited: boolean;
      description?: string;
      description_inherited: boolean;
    };
  };

  marketplace_titles?: {
    [marketplaceId: string]: {
      value: string;
      is_inherited: boolean;
    };
  };

  marketplace_short_descriptions?: {
    [marketplaceId: string]: {
      value: string;
      is_inherited: boolean;
    };
  };

  marketplace_descriptions?: {
    [marketplaceId: string]: {
      value: string;
      is_inherited: boolean;
    };
  };
  vats?: VatResource[];
  is_excluded?: string;
  priceListItems?: PriceListItemResource[];

  //includes
  variantsCount?: number;
  variantsExists?: string;
  attributeValuesCount?: number;
  attributeValuesExists?: string;

  images: { [uuid: string]: MediaAttributes };
}

export interface ProductPriceListItem {
  calculate_value: string;
  calculate_type: PriceListItemCalculateType;
  is_calculate_round: boolean;
  calculate_from: string;
}

export interface ProductStoreRequest {
  title: string;
  type: ProductType;
  sku: string;
  condition_id?: string | null;
  manufacturer_id?: string | null;
  short_description?: string | null;
  price?: string | null;
  long_description?: string | null;
  in_stock_information?: string | null;
  out_of_stock_information?: string | null;
  status?: "active" | "inactive" | "blocked";
  stock_flag?: ProductStockFlagEnum;
  processing_time_id?: string | null;
  is_short_description_printed_in_documents?: boolean | null;
  has_best_before_date?: boolean | null;
  is_intangible_product?: boolean | null;
  is_electron_return_option?: boolean | null;
  is_customizable_product?: boolean | null;
  weight?: string | null;
  weight_unit_id?: string | null;
  length?: string | null;
  width?: string | null;
  height?: string | null;
  size_unit_id?: string | null;
  volume?: string | null;
  volume_unit_id?: string | null;
  unit_quantity?: string | null;
  unit_id?: string | null;
  recommended_retail_price?: string | null;
  marketplaces?: string[];
  productGroups?: string[] | null;
  attributes?: string[] | null;
  vats?: { [countryId: string]: string } | null;
  ean?: string;
  blockedMarketplaces?: string[];

  // marketplace_titles?: {
  //   [marketplaceId: string]: {
  //     value?: string;
  //     is_inherited: boolean;
  //   };
  // };
  // marketplace_short_descriptions?: {
  //   [marketplaceId: string]: {
  //     value?: string;
  //     is_inherited: boolean;
  //   };
  // };
  // marketplace_descriptions?: {
  //   [marketplaceId: string]: {
  //     value?: string;
  //     is_inherited: boolean;
  //   };
  // };

  configNumbers?: {
    [id: string]: {
      value: string;
    };
  } | null;

  marketplaceData?: {
    [id: string]: {
      title?: string;
      title_inherited: boolean;
      short_description?: string;
      short_description_inherited: boolean;
      description?: string;
      description_inherited: boolean;
    };
  };
  images?: MediaAttributes[];

  attributeValues:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;
}

export type ProductUpdateRequest = {
  title?: string | null;
  sku?: string | null;
  condition_id?: string | null;
  manufacturer_id?: string | null;
  short_description?: string | null;
  price?: string | null;
  long_description?: string | null;
  in_stock_information?: string | null;
  out_of_stock_information?: string | null;
  status?: "active" | "inactive" | "blocked";
  stock_flag?: ProductStockFlagEnum;
  processing_time_id?: string | null;
  is_short_description_printed_in_documents?: boolean | null;
  has_best_before_date?: boolean | null;
  is_intangible_product?: boolean | null;
  is_electron_return_option?: boolean | null;
  is_customizable_product?: boolean | null;
  weight?: string | null;
  weight_unit_id?: string | null;
  length?: string | null;
  width?: string | null;
  height?: string | null;
  size_unit_id?: string | null;
  volume?: string | null;
  volume_unit_id?: string | null;
  unit_quantity?: string | null;
  unit_id?: string | null;
  recommended_retail_price?: string | null;
  marketplaces?: string[];
  productGroups?: string[] | null;
  attributes?: string[] | null;
  vats?: { [countryId: string]: string } | null;
  ean?: string;
  blockedMarketplaces?: string[];

  // marketplace_titles?: {
  //   [marketplaceId: string]: {
  //     value?: string;
  //     is_inherited: boolean;
  //   };
  // };
  // marketplace_short_descriptions?: {
  //   [marketplaceId: string]: {
  //     value?: string;
  //     is_inherited: boolean;
  //   };
  // };
  // marketplace_descriptions?: {
  //   [marketplaceId: string]: {
  //     value?: string;
  //     is_inherited: boolean;
  //   };
  // };

  configNumbers?: {
    [id: string]: {
      value: string;
    };
  } | null;

  marketplaceData?: {
    [id: string]: {
      title?: string;
      title_inherited: boolean;
      short_description?: string;
      short_description_inherited: boolean;
      description?: string;
      description_inherited: boolean;
    };
  };
  images?: { [uuid: string]: MediaAttributes };

  textFieldAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  floatAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  integerAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  dateAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  dateTimeAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  selectionListAttributeValues?:
    | {
        id: string;
        attribute_id: string;
      }[]
    | null;
};
